import configJSON from 'config.json';
import { getKinesisAmplifyConfig, recordKinesisEvent, makePropertyViewedEvent, makePropertyFavouritedEvent } from '@bedrock/analytics';
import { mergeAmplifyConfig } from 'services/amplify/configure';
import { isClientSide } from 'utils/host-config';
import { isStaging, isStageTwo, isVercel, isProduction } from 'utils/environment';
import { getIdentity } from './events/get-identity';

import type { PropertyInfo, PropertyViewedOptions, PropertyFavouritedInfo, PropertyFavouritedOptions } from '@bedrock/analytics';

export const KINESIS_CONFIGURED_KEY = '__KINESIS_ANALYTICS_CONFIGURED__';

export const KINESIS_ENABLED = (isProduction || isStaging || isStageTwo || isVercel) && isClientSide();

/**
 * Configures Amplify for Kinesis analytics. Safe to call multiple times; only the first successful call applies.
 */
export function configureKinesisAnalytics(): void {
  if (!KINESIS_ENABLED) return;
  if (getGlobalConfigured()) return;

  const identityPoolId = configJSON.analytics.kinesis.cognitoIdentityPoolId;
  const region = configJSON?.analytics?.kinesis?.region;
  const streamName = configJSON?.analytics?.kinesis?.streamName;
  const bufferSize = configJSON?.analytics?.kinesis?.bufferSize;
  const flushSize = configJSON.analytics.kinesis.flushSize;
  const flushInterval = configJSON?.analytics?.kinesis?.flushInterval;
  const resendLimit = configJSON?.analytics?.kinesis?.resendLimit;

  if (!identityPoolId || !region || !streamName) {
    console.error('[Data Stream] Missing Kinesis config (identityPoolId, region, or streamName).');
    return;
  }

  try {
    mergeAmplifyConfig(getKinesisAmplifyConfig({
      identityPoolId,
      region,
      bufferSize,
      flushSize,
      flushInterval,
      resendLimit,
    }));
    setGlobalConfigured();
  } catch (error) {
    console.error('[Data Stream] Configuration error:', error);
  }
}

/**
 * Records a property viewed event. No-op when disabled, not configured, identity/partition key
 * cannot be resolved, or when the event cannot be built.
 */
export function trackPropertyViewedEvent(property: PropertyInfo, options: PropertyViewedOptions): void {
  if (!KINESIS_ENABLED || !isClientSide()) return;

  if (!getGlobalConfigured()) {
    configureKinesisAnalytics();
    if (!getGlobalConfigured()) return;
  }

  const identity = getIdentity();
  if (identity === undefined) {
    console.error('[Data Stream] Failed to resolve identity for property viewed event.');
    return;
  }

  const result = makePropertyViewedEvent(property, options, identity);
  if (result === undefined) {
    console.error('[Data Stream] Failed to make property viewed event.');
    return;
  }

  try {
    recordKinesisEvent(configJSON?.analytics?.kinesis?.streamName, result.partitionKey, result.event);
  } catch (error) {
    console.error('[Data Stream] Failed to record property_viewed:', error);
  }
}

/**
 * Records a property favourited (add/remove) event. No-op when disabled, not configured,
 * identity/partition key cannot be resolved, or when the event cannot be built.
 * Non-blocking; safe to call from context after state update.
 */
export function trackPropertyFavouritedEvent(property: PropertyFavouritedInfo, options: PropertyFavouritedOptions): void {
  if (!KINESIS_ENABLED || !isClientSide()) return;

  if (!getGlobalConfigured()) {
    configureKinesisAnalytics();
    if (!getGlobalConfigured()) return;
  }

  const identity = getIdentity();
  if (identity === undefined) {
    console.error('[Data Stream] Failed to resolve identity for property favourited event.');
    return;
  }

  const result = makePropertyFavouritedEvent(property, options, identity);
  if (result === undefined) {
    console.error('[Data Stream] Failed to make property favourited event.');
    return;
  }

  try {
    recordKinesisEvent(configJSON.analytics.kinesis.streamName, result.partitionKey, result.event);
  } catch (error) {
    console.error('[Data Stream] Failed to record property_favourited:', error);
  }
}

export function isKinesisEnabled(): boolean {
  return KINESIS_ENABLED && getGlobalConfigured() && isClientSide();
}

//#region Private Functions
function getGlobalConfigured(): boolean {
  if (typeof window === 'undefined') return false;
  const isKinesisConfigured = (window as unknown as Record<string, boolean>)[KINESIS_CONFIGURED_KEY] === true;
  return isKinesisConfigured;
}

function setGlobalConfigured(): void {
  if (typeof window !== 'undefined') {
    (window as unknown as Record<string, boolean>)[KINESIS_CONFIGURED_KEY] = true;
  }
}
//#endregion
